import { lazy, Suspense, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { LogIn, UserCircle } from "lucide-react";
import { ShellButton } from "@/components/layout/ShellControl";
import { shellControlClassName } from "@/components/layout/shellControlClasses";
import { useAuth } from "@/contexts/AuthContext";

const SignedInAuthMenu = lazy(() => import("@/components/layout/SignedInAuthMenu"));

interface AuthMenuProps {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}

export function AuthMenu({ variant = "desktop", onNavigate }: AuthMenuProps) {
  const { user, profile, signOut, loading } = useAuth();

  const frame = (content: ReactNode) => (
    <div
      className={variant === "desktop"
        ? "flex h-10 w-28 shrink-0 items-center justify-end"
        : "flex h-10 w-10 shrink-0 items-center justify-end"}
      data-auth-menu-frame={variant}
    >
      {content}
    </div>
  );

  if (loading) {
    return frame(
      <ShellButton
        type="button"
        variant="ghost"
        size="icon"
        round
        className="ring-2 ring-primary/20"
        aria-label="Loading account menu"
        disabled
      >
        <UserCircle className="h-5 w-5" />
      </ShellButton>,
    );
  }

  // Signed-out state — prominent Sign In CTA
  if (!user) {
    if (variant === "mobile") {
      return frame(
        <Link
          to="/account"
          onClick={onNavigate}
          aria-label="Sign in"
          title="Sign in"
          className={shellControlClassName({ variant: "ghost", size: "icon" })}
        >
          <LogIn className="h-5 w-5" />
        </Link>,
      );
    }

    return frame(
      <Link
        to="/account"
        onClick={onNavigate}
        className={shellControlClassName({
          size: "sm",
          className: "font-semibold shadow-sm",
        })}
      >
        <LogIn className="h-4 w-4" />
        Sign In
      </Link>,
    );
  }

  return frame(
    <Suspense
      fallback={
        <ShellButton
          type="button"
          variant="ghost"
          size="icon"
          round
          className="ring-2 ring-primary/20"
          aria-label="Loading account menu"
          disabled
        >
          <UserCircle className="h-5 w-5" />
        </ShellButton>
      }
    >
      <SignedInAuthMenu
        user={user}
        profile={profile}
        signOut={signOut}
        onNavigate={onNavigate}
      />
    </Suspense>,
  );
}
